import { useEffect, useState } from 'react';
import {
  DisputeRoutingRule,
  DisputeRoutingRuleAssigneeMode,
  DisputeRoutingRuleCreate,
  TaskPriority,
} from '@dill/dill-shared';
import {
  DillModal,
  DillButton,
  DillInput,
  DillField,
  DillSelect,
  DillToggle,
  DillDropdown,
} from '../../../general/Dill';

interface DisputeRoutingRuleFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: DisputeRoutingRuleCreate) => void;
  initialData?: DisputeRoutingRule | null;
  isLoading: boolean;
  errorMessage?: string | null;
  categoryOptions: { label: string; value: string }[];
  supplierUsers: { id: string; firstName?: string | null; lastName?: string | null }[];
}

const ASSIGNEE_MODE_OPTIONS: { label: string; value: DisputeRoutingRuleAssigneeMode }[] = [
  { label: 'Specific people', value: 'SPECIFIC_USERS' },
  { label: "The customer's sales rep", value: 'CUSTOMER_SALES_REP' },
  { label: 'Tax Exemption Team', value: 'TAX_EXEMPTION_TEAM' },
];

const AUTO_RESOLVED_ASSIGNEE_MODES: DisputeRoutingRuleAssigneeMode[] = [
  'CUSTOMER_SALES_REP',
  'TAX_EXEMPTION_TEAM',
];

const PRIORITY_OPTIONS: { label: string; value: TaskPriority }[] = [
  { label: 'Low priority', value: 'LOW' },
  { label: 'Medium priority', value: 'MEDIUM' },
  { label: 'High priority', value: 'HIGH' },
];

export const DisputeRoutingRuleFormModal = ({
  open,
  onClose,
  onSubmit,
  initialData,
  isLoading,
  errorMessage,
  categoryOptions,
  supplierUsers,
}: DisputeRoutingRuleFormModalProps) => {
  const [name, setName] = useState('');
  const [disputeCategory, setDisputeCategory] = useState('');
  const [assigneeMode, setAssigneeMode] =
    useState<DisputeRoutingRuleAssigneeMode>('SPECIFIC_USERS');
  const [assignToUserIds, setAssignToUserIds] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [priority, setPriority] = useState<TaskPriority>('MEDIUM');
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const isEditMode = !!initialData;

  useEffect(() => {
    if (!open) {
      return;
    }
    setHasAttemptedSubmit(false);
    if (initialData) {
      setName(initialData.name || '');
      setDisputeCategory(initialData.disputeCategory || '');
      setAssigneeMode(initialData.assigneeMode || 'SPECIFIC_USERS');
      setAssignToUserIds(initialData.assignToUserIds || []);
      setIsActive(initialData.isActive !== false);
      setPriority(initialData.priority || 'MEDIUM');
      setTaskTitle(initialData.taskTitle || '');
      setTaskDescription(initialData.taskDescription || '');
    } else {
      setName('');
      setDisputeCategory('');
      setAssigneeMode('SPECIFIC_USERS');
      setAssignToUserIds([]);
      setIsActive(true);
      setPriority('MEDIUM');
      setTaskTitle('');
      setTaskDescription('');
    }
  }, [open, initialData]);

  const userOptions = supplierUsers.map((u) => ({
    label: [u.firstName, u.lastName].filter(Boolean).join(' ') || 'Unnamed teammate',
    value: u.id,
  }));

  const isNameValid = name.trim().length > 0;
  const isCategoryValid = disputeCategory.trim().length > 0;
  const isUsersValid =
    AUTO_RESOLVED_ASSIGNEE_MODES.includes(assigneeMode) || assignToUserIds.length > 0;
  const isValid = isNameValid && isCategoryValid && isUsersValid;

  const showError = (fieldIsValid: boolean) => hasAttemptedSubmit && !fieldIsValid;

  const handleSubmit = () => {
    if (!isValid) {
      setHasAttemptedSubmit(true);
      return;
    }
    onSubmit({
      supplierId: '', // filled in by the caller
      name: name.trim(),
      disputeCategory,
      assigneeMode,
      assignToUserIds,
      isActive,
      priority,
      taskTitle: taskTitle.trim() || null,
      taskDescription: taskDescription.trim() || null,
    } as DisputeRoutingRuleCreate);
  };

  return (
    <DillModal
      open={open}
      onClose={onClose}
      title={isEditMode ? 'Edit dispute routing rule' : 'New dispute routing rule'}
      subtitle="Automatically creates a task for the right person whenever a dispute with this category is filed."
      maxWidth="480px"
      footer={
        <>
          <DillButton label="Cancel" variant="secondary" onClick={onClose} />
          <DillButton
            label={isEditMode ? 'Save changes' : 'Create rule'}
            onClick={handleSubmit}
            isDisabled={!isValid}
            isLoading={isLoading}
          />
        </>
      }>
      {errorMessage && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 m-0">
          {errorMessage}
        </p>
      )}

      <DillField
        label="Rule name"
        required
        error={showError(isNameValid) ? 'A name is required' : undefined}>
        <DillInput value={name} onChange={setName} placeholder="e.g. Tax Exemption Review" />
      </DillField>

      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-600">
          When a dispute is created with category
        </label>
        <DillSelect
          value={disputeCategory}
          onChange={setDisputeCategory}
          options={categoryOptions}
          placeholder="Select a dispute category"
          error={showError(isCategoryValid) ? 'A category is required' : undefined}
        />
      </div>

      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-gray-600">Active</label>
        <DillToggle value={isActive} onChange={setIsActive} />
      </div>

      <div className="border-t border-gray-100 pt-3 flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-600">Assign a review task to</label>
        <DillSelect
          value={assigneeMode}
          onChange={(v) => setAssigneeMode(v as DisputeRoutingRuleAssigneeMode)}
          options={ASSIGNEE_MODE_OPTIONS}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-600">
          {AUTO_RESOLVED_ASSIGNEE_MODES.includes(assigneeMode)
            ? `Fallback people (used if ${
                assigneeMode === 'CUSTOMER_SALES_REP' ? 'a sales rep' : 'no team member'
              } cannot be found)`
            : 'People'}
        </label>
        <DillDropdown
          items={userOptions}
          value={null}
          isMulti
          showSelectedItems
          multiValue={assignToUserIds}
          onMultiSelectChange={(newValue) => setAssignToUserIds(newValue.map((v) => v.value))}
          placeholder="Search teammates…"
          containerClassName="h-[36px]"
          showMinimalDesign
          numberOfItemsToShow={3}
        />
        {showError(isUsersValid) && (
          <p className="text-[11px] text-red-500 m-0">At least one person is required</p>
        )}
        {assigneeMode === 'CUSTOMER_SALES_REP' && (
          <p className="text-[11px] text-gray-400 m-0 mt-1">
            Resolved from the disputed invoice's sales rep, when that rep has been linked to a Dill
            account in Branch Settings. Optional otherwise — leave blank to skip the task rather
            than assign it to nobody.
          </p>
        )}
        {assigneeMode === 'TAX_EXEMPTION_TEAM' && (
          <p className="text-[11px] text-gray-400 m-0 mt-1">
            Assigns the task to every active member of the Tax Exemption Team (Supplier Settings)
            who has Dill access. Optional otherwise — leave blank to skip the task rather than
            assign it to nobody.
          </p>
        )}
      </div>

      <div className="border-t border-gray-100 pt-3 flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-600">Task priority</label>
        <DillSelect
          value={priority}
          onChange={(v) => setPriority(v as TaskPriority)}
          options={PRIORITY_OPTIONS}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-600">Task title (optional)</label>
        <input
          value={taskTitle}
          onChange={(e) => setTaskTitle(e.target.value)}
          placeholder="e.g. Review {{category}} dispute for {{buyerName}}"
          className="h-9 w-full border border-gray-300 rounded-lg bg-white px-3 text-sm text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-50 focus:border-blue-400 transition-all"
        />
        <p className="text-[11px] text-gray-400 mt-1 mb-0">
          {'{{buyerName}}'}, {'{{category}}'} and {'{{invoiceNumber}}'} get filled in automatically.
          Leave blank to use a default title.
        </p>
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-600">Task description (optional)</label>
        <textarea
          value={taskDescription}
          onChange={(e) => setTaskDescription(e.target.value)}
          rows={3}
          placeholder="Optional — add any additional context for whoever picks this up"
          className="w-full border border-gray-300 rounded-lg bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-50 focus:border-blue-400 resize-none transition-all"
        />
      </div>
    </DillModal>
  );
};

export default DisputeRoutingRuleFormModal;
